'use client';

import {
	Box,
	FormControl,
	FormHelperText,
	FormLabel,
	TextFieldProps,
} from '@mui/material';
import React from 'react';
import MuiCheckbox, { MuiCheckboxProps } from '../../mui/MuiCheckbox';
import ExtendedLink from '../ExtendedLink/ExtendedLink';
import { gql } from '@apollo/client';
import { useTermsOfService_GetGlobalVariablesQuery } from '#types';
import styles from './TermsOfService.module.scss';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getGlobalVariablesQuery = gql`
	query TermsOfService_getGlobalVariables {
		getGlobalVariables {
			StripeLegalUrl
			TermsOfServiceUrl
		}
	}
`;

export type TermsOfServiceProps = {
	termsOfServiceAccepted?: boolean;
	setTermsOfServiceAccepted: React.Dispatch<React.SetStateAction<boolean>>;
	termsOfServiceAcceptedValidation?: string;
	consentedToAgeAndMarketingEmails?: boolean;
	setConsentedToAgeAndMarketingEmails: React.Dispatch<
		React.SetStateAction<boolean>
	>;
	consentedToAgeAndMarketingEmailsValidation?: string;
	onBlur?: TextFieldProps['onBlur'] & MuiCheckboxProps['onBlur'];
	includeConnectStatement?: true;
};

export const TermsOfService: React.FC<TermsOfServiceProps> = ( {
	termsOfServiceAccepted,
	setTermsOfServiceAccepted,
	termsOfServiceAcceptedValidation,
	consentedToAgeAndMarketingEmails,
	setConsentedToAgeAndMarketingEmails,
	consentedToAgeAndMarketingEmailsValidation,
	onBlur,
	includeConnectStatement,
} ) => {
	const { data: globalVariableData, loading: globalVariableLoading } =
		useTermsOfService_GetGlobalVariablesQuery();

	const legalHref = globalVariableData?.getGlobalVariables.StripeLegalUrl;
	const termsOfServiceHref =
		globalVariableData?.getGlobalVariables.TermsOfServiceUrl;
	return (
		<Box className={ styles.termsOfServiceContainer }>
			<FormControl
				error={ !!termsOfServiceAcceptedValidation }
				className={ styles.termsOfService }
			>
				<Box className={ styles.termsOfServiceBody }>
					<MuiCheckbox
						id='termsOfService'
						checked={ termsOfServiceAccepted || false }
						onChange={ ( _, checked ) => setTermsOfServiceAccepted( checked ) }
						onBlur={ onBlur }
						slotProps={ {
							input: {
								'data-cy': 'termsOfServiceAccepted',
							},
						} }
						error={ !!termsOfServiceAcceptedValidation }
					/>
					<FormLabel htmlFor='termsOfService'>
						I have reviewed and agree to Rock Paper Coin's{ ' ' }
						<ExtendedLink
							target='_blank'
							href={ termsOfServiceHref }
							rel='noreferrer'
							loading={ globalVariableLoading }
						>
							Terms and Conditions
						</ExtendedLink>
						{ includeConnectStatement ? (
							<>
								{ ' ' }
								and Stripe Connect's{ ' ' }
								<ExtendedLink
									target='_blank'
									href={ legalHref }
									rel='noreferrer'
									loading={ globalVariableLoading }
								>
									Account Agreement
								</ExtendedLink>
							</>
						) : null }
					</FormLabel>
				</Box>
				{ termsOfServiceAcceptedValidation ? (
					<FormHelperText>{ termsOfServiceAcceptedValidation }</FormHelperText>
				) : null }
			</FormControl>
			<FormControl
				error={ !!consentedToAgeAndMarketingEmailsValidation }
				className={ styles.termsOfService }
			>
				<Box className={ styles.termsOfServiceBody }>
					<MuiCheckbox
						id='consentedToAgeAndMarketingEmails'
						checked={ consentedToAgeAndMarketingEmails || false }
						onChange={ ( _, checked ) =>
							setConsentedToAgeAndMarketingEmails( checked )
						}
						onBlur={ onBlur }
						slotProps={ {
							input: {
								'data-cy': 'consentedToAgeAndMarketingEmails',
							},
						} }
						error={ !!consentedToAgeAndMarketingEmailsValidation }
					/>
					<FormLabel htmlFor='consentedToAgeAndMarketingEmails'>
						By accepting these terms, you confirm that you are at least 18 years
						old and consent to receive marketing communications from Rock Paper
						Coin.
					</FormLabel>
				</Box>
				{ consentedToAgeAndMarketingEmailsValidation ? (
					<FormHelperText>
						{ consentedToAgeAndMarketingEmailsValidation }
					</FormHelperText>
				) : null }
			</FormControl>
		</Box>
	);
};
